import { useRouter } from 'next/router'
import { GetStaticPaths, GetStaticProps } from 'next'
import { getAllPagesWithSlug, getPageForSlug } from "../../lib/api";
import { extractFaqSchemaFromHtml } from "../../lib/faqSchema";
import Layout from "../../components/layout";
import ErrorPage404 from "../../components/404";
import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import Industry from '../../templates/solutions/industry';
import AwsSolutions from '../../templates/solutions/aws';
import GCPSolutions from "../../templates/solutions/gcp";
import AzureSolutions from "../../templates/solutions/azure";
import Retail from "../../templates/solutions/retail";
import Finance from "../../templates/solutions/finance";
import Gaming from "../../templates/solutions/gaming";
import FrontToBackendCorrelation from "../../templates/solutions/front-to-backend-correlation";
import LogAnalysisCorrelation from "../../templates/solutions/log-analysis-correlation";
import TechnologySolution from "../../templates/solutions/technology-solution";
import DEM from "../../templates/solutions/dem";
import KubernetesMonitoring from '../../templates/product/kubernetes-monitoring';
import EndpointMonitoring from '../../templates/solutions/endpoint-monitoring';
import nextI18NextConfig from "../../next-i18next.config"
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import "../../styles/scss/solutions.module.scss";

const PARENT_PAGE = 'solutions';

const PAGE_COMPONENTS = {
    'digital-experience-monitoring': dynamic(() => import('../../templates/solutions/digital')),
    'industry': dynamic(() => import('../../templates/solutions/industry')),
    'technology': dynamic(() => import('../../templates/solutions/technology')),
    'aws': dynamic(() => import('../../templates/solutions/aws')),
};

const CommonPageTemplate = dynamic(() => import('../../templates/common'));

const SolutionPage = ({ pageData }) => {
    const router = useRouter();

    if (router.isFallback) {
        return <div>Loading...</div>;
    }

    const page = pageData?.pages?.edges?.[0]?.node;

    if (!page?.slug) {
        return <ErrorPage404 />;
    }

    const PageComponent = useMemo(() => PAGE_COMPONENTS[page.slug] || CommonPageTemplate, [page.slug]);
    let component

    switch (page?.slug) {
        case "industry":
            component = <Industry />;
            break;

        case "aws":
            component = <AwsSolutions />;
            break;

        case "azure":
            component = <AzureSolutions />;
            break;

        case "gcp":
            component = <GCPSolutions />;
            break;

        case "finance":
            component = <Finance />;
            break;

        case "retail":
            component = <Retail />;
            break;

        case "gaming":
            component = <Gaming />;
            break;

        case "frontend-backend-correlation":
            component = <FrontToBackendCorrelation />;
            break;

        case "log-analysis-correlation":
            component = <LogAnalysisCorrelation />;
            break;

        case "technology":
            component = <TechnologySolution />;
            break;

        case "digital-experience-monitoring":
            component = <DEM />;
            break;

        case "kubernetes-monitoring":
            component = <KubernetesMonitoring />;
            break;

        case "endpoint-monitoring":
            component = <EndpointMonitoring />;
            break;

        default:
            component = <CommonPageTemplate pageData={pageData} />;
    }
    return (
        <>
            <Layout isHomePage={page?.isFrontPage} pageTitle={page?.title}
                uri={page?.uri}
                ogImage={page?.featuredImage?.node?.sourceUrl}
                metaTitle={page?.seo?.title}
                metaDesc={page?.seo?.metaDesc}
                metaRobotsNoindex={page?.seo?.metaRobotsNoindex}
                metaRobotsNofollow={page?.seo?.metaRobotsNofollow}>
                <div className={`middle-wrapper site-page ${PARENT_PAGE} ${page.slug}-page`}>
                    {component}
                    <CallToActionSmall />
                </div>
            </Layout>
        </>
    )
}


export const getStaticProps: GetStaticProps = async ({
    params,
    locale,
    preview = false,
    previewData,
}) => {
    const data = await getPageForSlug(params?.slug, PARENT_PAGE)
    let page = data?.pages?.edges?.[0]?.node

    const content = data?.pages?.edges?.[0]?.node?.content || "";
    const faqSchema = extractFaqSchemaFromHtml(content);

    // Check if page has dynamic FAQ content
    const hasDynamicFAQ = [
        "log-analysis-correlation",
        "frontend-backend-correlation",
        "aws",
        "azure",
        "gcp",
        "dem",
        "digital"
    ].includes(page?.slug);

    return {
        props: {
            pageData: data,
            pageTitle: page?.seo?.title,
            description: page?.seo?.metaDesc,
            ogImage: page?.featuredImage?.node?.sourceUrl || null,
            uri: page?.uri,
            metaTitle: page?.seo?.title,
            metaDesc: page?.seo?.metaDesc,
            metaRobotsNoindex: page?.seo?.metaRobotsNoindex,
            metaRobotsNofollow: page?.seo?.metaRobotsNofollow,
            faqSchema: faqSchema,
            hasDynamicFAQ: hasDynamicFAQ,
            ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer', 'home'], nextI18NextConfig)),
        },
        // revalidate: 10,
    }
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
    const allPages = await getAllPagesWithSlug();
    const pageSlugs = allPages.edges
        .filter((item) => item.node?.parent?.node.slug === PARENT_PAGE)
        .map((item) => item.node.slug);

    // Generate paths for all locales
    const paths = pageSlugs.flatMap((slug) =>
        (locales || ['en']).map((locale) => ({
            params: { slug },
            locale,
        }))
    );

    return {
        paths,
        fallback: false, // Enable blocking fallback for better performance
    };
};

export default SolutionPage;

